import { useMemo } from 'react'

import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useMarketInfoQuery } from '@/domain/market-info/useMarketInfo'
import { useHyperCoreSpotMetaQuery } from '@/domain/wallet/hypercore/useHyperCoreSpotMeta'
import { useL1BalancesQuery } from '@/domain/wallet/hypercore/useL1Balances'
import { formatL1Tokens } from '@/features/side-drawer/logic/utils'

import type { UsePortfolioL1BalancesResult } from './types'

export function usePortfolioL1Balances({
  chainId: chainIdOverride,
  refetchInterval,
}: { chainId?: number; refetchInterval?: number } = {}): UsePortfolioL1BalancesResult {
  const { chainId: pageChainId } = usePageChainId()
  const chainId = chainIdOverride ?? pageChainId
  const nativeAssetInfo = getNativeAssetInfo(chainId)

  const { marketInfo, isPending: isPooledMarketLoading } = useMarketInfoQuery({ chainId, refetchInterval })
  const { balances: l1Balances, isPending: isL1WalletLoading } = useL1BalancesQuery({ refetchInterval: 30000 })
  const { assets: hyperCoreAssets, isPending: isHyperCoreAssetsLoading } = useHyperCoreSpotMetaQuery()

  const balances = useMemo(() => {
    if (!marketInfo) return []
    return formatL1Tokens({
      l1Balances,
      hyperCoreAssets,
      marketInfo,
      nativeAssetInfo,
    })
  }, [hyperCoreAssets, l1Balances, marketInfo, nativeAssetInfo])

  const loading = {
    pooledMarket: isPooledMarketLoading,
    l1Wallet: isL1WalletLoading,
    hyperCoreAssets: isHyperCoreAssetsLoading,
  }

  return {
    balances,
    marketInfo,
    loading,
    isLoading: Object.values(loading).some(Boolean),
  }
}
